import { CollectionFile } from "../../../../shared/types/files";

export interface SelectedAssetsSidebarProps {
  selectedAssets: CollectionFile[];
  maxAssets: number;
  /**
   * The most one post can hold. When given, the counter reads against it
   * until the selection passes it, then against `maxAssets` as bulk-only
   * (SKE-4715).
   */
  postLimit?: number;
  onRemoveAsset: (asset: CollectionFile) => void;
}

export function SelectedAssetsSidebar({
  selectedAssets,
  maxAssets,
  postLimit,
  onRemoveAsset,
}: SelectedAssetsSidebarProps) {
  const selectedCount = selectedAssets.length;
  const bulkOnly = postLimit !== undefined && selectedCount > postLimit;
  const shownLimit =
    postLimit !== undefined && !bulkOnly ? postLimit : maxAssets;

  const placeholderCount = bulkOnly
    ? 0
    : Math.max(0, shownLimit - selectedCount);
  const placeholders = Array.from({ length: placeholderCount }, (_, i) => i);

  return (
    <div className="flex h-full w-[220px] shrink-0 flex-col bg-white">
      <div className="px-6 py-4">
        <div className="flex items-center gap-2 font-inter text-base leading-6">
          <span className="font-semibold text-neutral-900">Selected</span>
          <span className="font-normal text-neutral-500">
            {selectedCount}/{shownLimit}
          </span>
        </div>
        {bulkOnly && (
          <p className="mt-1 font-inter text-xs text-neutral-600">
            Bulk posts only
          </p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-6 pb-4">
        <div className="grid grid-cols-3 content-start gap-2">
          {selectedAssets.map((asset) => {
            const thumbnailSrc =
              asset.thumbnail || asset.storage?.url || asset.downloadUrl;

            return (
              <button
                key={asset._id}
                onClick={() => onRemoveAsset(asset)}
                className="group relative h-[52px] w-[52px] overflow-hidden rounded-lg border border-neutral-200 bg-neutral-25 p-0"
                aria-label="Remove asset"
              >
                <img
                  src={thumbnailSrc}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 z-[0] h-full w-full scale-110 object-cover blur-[10px]"
                />
                <div className="absolute inset-0 z-[1] bg-black/5" />
                <img
                  src={thumbnailSrc}
                  alt={asset.name || "Selected asset"}
                  className="absolute inset-0 z-[2] h-full w-full object-contain"
                />

                <div className="absolute inset-0 z-[3] flex items-center justify-center bg-black/60 opacity-0 transition-opacity group-hover:opacity-100">
                  <i className="fa-regular fa-trash-can text-base text-white" />
                </div>
              </button>
            );
          })}

          {placeholders.map((index) => (
            <div
              key={`placeholder-${index}`}
              className="h-[52px] w-[52px] rounded-lg border border-dashed border-neutral-200 bg-neutral-25"
            />
          ))}
        </div>
      </div>
    </div>
  );
}
